import { useConditionalRender } from '@finanzcheck/catalyst-conditional-component'
import { logWarning } from 'utils/log'
import { executeAsync } from 'utils/performance'
import { useStores } from 'stores/utils/hooks/useStores'
import { usePrevious } from 'hooks/usePrevious'
import { observer } from 'mobx-react'
import { collectListForAmplitude } from 'utils/tracking/amplitude'
import { memo, PropsWithChildren, useEffect } from 'react'
import { AmplitudeEvent } from '../../utils/tracking/amplitude/amplitude.interface'

interface ConditionalFieldProps {
  path: string
}

const ConditionalField = observer(
  ({
    children,
    path
  }: PropsWithChildren<ConditionalFieldProps>): JSX.Element | null => {
    const isVisible = useConditionalRender(path)
    const {
      traversal: { removeErrorsForPath }
    } = useStores()
    const wasVisible = usePrevious(isVisible)

    useEffect(() => {
      if (wasVisible === isVisible) {
        return
      }

      if (!path) {
        logWarning("ConditionalField: Context doesn't contain any path.")
        return
      }

      if (!wasVisible && isVisible) {
        removeErrorsForPath(path)
      }

      executeAsync(() => {
        collectListForAmplitude(
          AmplitudeEvent.ConditionalFields,
          'status',
          `${path}: ${wasVisible || 'initial'} -> ${isVisible}`
        )
      })
    }, [isVisible, path, removeErrorsForPath, wasVisible])

    return isVisible ? <>{children}</> : null
  }
)

export default memo(ConditionalField)
